"use client";
import React, { useEffect, useRef } from "react";

export interface HlsVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  poster?: string;
}

/**
 * A video element that supports HLS (.m3u8) streams via hls.js.
 * Safari supports HLS natively; for other browsers hls.js is loaded on demand.
 * Falls back to a plain <video> for non-HLS sources (mp4, webm, etc.).
 */
const HlsVideo = React.forwardRef<HTMLVideoElement, HlsVideoProps>(
  ({ src, poster, ...rest }, forwardedRef) => {
    const internalRef = useRef<HTMLVideoElement | null>(null);
    const hlsRef = useRef<any>(null);

    const setRef = (el: HTMLVideoElement | null) => {
      internalRef.current = el;
      if (typeof forwardedRef === "function") forwardedRef(el);
      else if (forwardedRef) forwardedRef.current = el;
    };

    useEffect(() => {
      const video = internalRef.current;
      if (!video || !src) return;

      const isHls =
        src.includes(".m3u8") || src.includes("manifest/video.m3u8");

      // Non-HLS source — just set src directly
      if (!isHls) {
        video.src = src;
        return;
      }

      // Safari / iOS support HLS natively
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src;
        return;
      }

      // Dynamically load hls.js for other browsers
      let cancelled = false;
      import("hls.js").then((mod) => {
        if (cancelled) return;
        const Hls = mod.default;
        if (!Hls.isSupported()) {
          // Last resort — try native playback
          video.src = src;
          return;
        }
        const hls = new Hls({ enableWorker: false });
        hlsRef.current = hls;
        hls.loadSource(src);
        hls.attachMedia(video);
      });

      return () => {
        cancelled = true;
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      };
    }, [src]);

    return <video ref={setRef} poster={poster} {...rest} />;
  },
);

HlsVideo.displayName = "HlsVideo";

export default HlsVideo;
